import React from 'react';
import { BRAND_COLORS } from '../types/index.ts';
import { fillBarColor } from './fillColors.ts';

interface FillCubeProps {
  utilization: number; // 0..100
  size?: number; // px width
  animate?: boolean;
  onDark?: boolean; // when placed on the dark console header, so the wireframe stays visible
}

// A small axonometric wireframe box that fills from the floor with a green
// voxel lattice to the fill %. It makes the metric spatial and visually rhymes
// with the real three.js render below it. NOTE: this is a fixed iso-lattice
// masked to the fill height — deliberately NOT a computed voxel stack.
const FillCube: React.FC<FillCubeProps> = ({ utilization, size = 56, animate = true, onDark = false }) => {
  const u = Math.max(0, Math.min(100, utilization));
  const fill = fillBarColor(u);
  // On the dockBlue header the dockBlue wireframe would be invisible (dark-on-dark);
  // switch it to white there so the box outline still frames the fill.
  const wireStroke = onDark ? 'rgba(255,255,255,0.9)' : BRAND_COLORS.dockBlue;
  const uid = React.useId().replace(/:/g, '');
  const clipId = `fillcube-clip-${uid}`;

  // Box silhouette (hexagon) in a 56×64 viewBox; vertical extent y=6..58.
  const topY = 6;
  const botY = 58;
  const fillTop = botY - (botY - topY) * (u / 100);
  const silhouette = '28,6 52,19 52,45 28,58 4,45 4,19';

  return (
    <svg
      width={size}
      height={Math.round((size * 64) / 56)}
      viewBox="0 0 56 64"
      role="img"
      aria-label={`Fill ${u.toFixed(0)} percent`}
    >
      <defs>
        <clipPath id={clipId}>
          <polygon points={silhouette} />
        </clipPath>
      </defs>

      {/* fill: rises from the floor, clipped to the box silhouette */}
      <g clipPath={`url(#${clipId})`}>
        <g
          style={{
            transformBox: 'fill-box',
            transformOrigin: 'bottom',
            animation: animate ? 'ld-rise-y 500ms cubic-bezier(0.22,1,0.36,1) both' : 'none',
          }}
        >
          <rect x="0" y={fillTop} width="56" height={64 - fillTop} fill={fill} opacity={0.9} />
          {/* voxel graduations */}
          <g stroke="rgba(255,255,255,0.35)" strokeWidth="1">
            <line x1="0" y1="24" x2="56" y2="24" />
            <line x1="0" y1="34" x2="56" y2="34" />
            <line x1="0" y1="44" x2="56" y2="44" />
            <line x1="0" y1="54" x2="56" y2="54" />
            <line x1="18" y1="10" x2="18" y2="64" />
            <line x1="38" y1="10" x2="38" y2="64" />
          </g>
        </g>
      </g>

      {/* wireframe box on top */}
      <g fill="none" stroke={wireStroke} strokeWidth="1.6" strokeLinejoin="round">
        <polygon points={silhouette} />
        <line x1="28" y1="6" x2="28" y2="32" />
        <line x1="4" y1="19" x2="28" y2="32" />
        <line x1="52" y1="19" x2="28" y2="32" />
      </g>
    </svg>
  );
};

export default FillCube;
